/**
 * Navattic interactive demo embeds — framework-agnostic core.
 *
 * Navattic ships `https://js.navattic.com/embeds.js`, which exposes a global
 * `window.NavatticEmbed` and renders demos in a `<navattic-lightbox>` custom
 * element appended to `document.body`. Its declarative mode (`data-navattic-open`
 * attributes + `initPopups()`) scans the DOM exactly once on page load, has no
 * MutationObserver and no guard against double binding, so it does not work
 * with client-side navigation in Next.js. This module drives the imperative
 * API instead:
 *
 *   loadNavatticEmbeds()  → inject the script once, resolve with the API
 *   openNavatticDemo()    → initPopup(preload:false) once per demo, then openPopup()
 *   resolveNavatticDemo() → pick the mobile or desktop demo at click time
 *
 * Both apps (arrive via a React trigger component, parkmobile via a plain
 * `onClick` from the Storyblok button resolver) share this file, so it must
 * stay free of React and JSX. Never call `NavatticEmbed.initPopups()` from here
 * or anywhere else in the apps.
 */

export const NAVATTIC_EMBED_SCRIPT_SRC = "https://js.navattic.com/embeds.js";
export const NAVATTIC_SCRIPT_ID = "navattic-embeds";
export const NAVATTIC_DEFAULT_ALLOW = "fullscreen; autoplay";

/**
 * Where the mobile demo and the full-screen lightbox apply: below Tailwind's
 * `md` (768px), plus phones in landscape, which are wider than that but far
 * too short for the desktop demo.
 */
export const NAVATTIC_MOBILE_MEDIA_QUERY =
  "(max-width: 767px), (pointer: coarse) and (max-height: 500px)";

const NAVATTIC_HOST_SUFFIX = ".navattic.com";
const LIGHTBOX_ID_PREFIX = "navattic-lightbox-";
// Short on purpose: when the script fails, the fallback must still run inside
// the click's transient user activation (~5 s in Chromium), see openNavatticDemoOrFallback.
const SCRIPT_LOAD_TIMEOUT_MS = 5_000;

export type NavatticPopupOptions = {
  title?: string;
  preload?: boolean;
  allow?: string;
  onClose?: () => void;
};

/** Subset of the `NavatticEmbed` static class exposed by embeds.js. */
export interface NavatticEmbedApi {
  initPopup(url: string, options?: NavatticPopupOptions): void;
  openPopup(url: string): void;
  preloadPopup?(url: string, title?: string): void;
  initPopups?(): void;
  loadEmbeds?(): void;
}

declare global {
  interface Window {
    NavatticEmbed?: NavatticEmbedApi;
  }
}

/**
 * Mirrors Navattic's own id extraction (`new URL(url).pathname.split("/").pop()`)
 * so that our lightbox lookups match the elements the embed script creates.
 */
export function getNavatticDemoId(url: string): string | null {
  try {
    const last = new URL(url).pathname.split("/").pop();
    return last ? last : null;
  } catch {
    return null;
  }
}

/**
 * Only `https://*.navattic.com/<demoId>` URLs may be opened. The value comes from
 * the CMS, and the embed script will happily iframe any URL it is given, so this
 * is the guard that keeps editors from embedding arbitrary third-party pages.
 */
export function isAllowedNavatticUrl(url: unknown): url is string {
  if (typeof url !== "string" || url.trim() === "") return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== "https:") return false;
  if (parsed.username || parsed.password) return false;

  const host = parsed.hostname.toLowerCase();
  if (!host.endsWith(NAVATTIC_HOST_SUFFIX)) return false;
  if (host.length === NAVATTIC_HOST_SUFFIX.length) return false; // bare "navattic.com"

  return getNavatticDemoId(url) !== null;
}

/** True when the visitor should get the mobile demo. Browser only. */
export function isNavatticMobileViewport(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean(window.matchMedia?.(NAVATTIC_MOBILE_MEDIA_QUERY).matches)
  );
}

export type NavatticDemoVariants = {
  url: unknown;
  title?: string;
  /** Optional mobile flow; falls back to `url`, like a mobile image. */
  mobileUrl?: unknown;
};

/**
 * Picks the demo to open. Call it at click/preload time, never during render:
 * the viewport is unknown on the server, and a phone rotated while a demo is
 * open keeps the demo it started with. An invalid mobile URL is ignored.
 * Both variants share the lightbox title.
 */
export function resolveNavatticDemo(
  { url, title, mobileUrl }: NavatticDemoVariants,
  isMobile: boolean = isNavatticMobileViewport(),
): { url: unknown; title?: string } {
  if (isMobile && isAllowedNavatticUrl(mobileUrl)) {
    return { url: mobileUrl, title };
  }
  return { url, title };
}

/**
 * Navattic's lightbox is built for desktop: on a phone the modal is 92vh tall
 * under a 16px top and 80px bottom padding (so it scrolls and ends up behind
 * the iOS toolbar) and the close button is 34×26px. On mobile viewports this
 * makes it full-screen (`dvh`, safe areas); any device with a touch screen,
 * tablets and touch laptops included, gets a 44×44px close button.
 * Navattic's own rules sit in cascade layers, so these unlayered ones win
 * without `!important`. The ids come from Navattic's shadow DOM template.
 */
const MOBILE_LIGHTBOX_STYLE_ID = "navattic-mobile-layout";
const MOBILE_LIGHTBOX_CSS = `
@media ${NAVATTIC_MOBILE_MEDIA_QUERY} {
  #background { min-height: 0; height: 100%; padding: 0; }
  #background > span { display: none; }
  #modal {
    width: 100%;
    max-width: none;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    box-shadow: none;
    vertical-align: top;
    padding: env(safe-area-inset-top) env(safe-area-inset-right)
      env(safe-area-inset-bottom) env(safe-area-inset-left);
  }
  #modal > :first-child { height: 52px; flex-shrink: 0; padding-inline: 8px 4px; }
}
@media ${NAVATTIC_MOBILE_MEDIA_QUERY}, (any-pointer: coarse) {
  #close-demo {
    min-width: 44px;
    min-height: 44px;
    justify-content: center;
    touch-action: manipulation;
  }
}`;

function applyMobileLayout(lightbox: HTMLElement | null) {
  const root = lightbox?.shadowRoot;
  if (!root || root.getElementById(MOBILE_LIGHTBOX_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = MOBILE_LIGHTBOX_STYLE_ID;
  style.textContent = MOBILE_LIGHTBOX_CSS;
  root.appendChild(style);
}

function getLightboxElement(demoId: string): HTMLElement | null {
  return document.getElementById(`${LIGHTBOX_ID_PREFIX}${demoId}`);
}

let scriptPromise: Promise<NavatticEmbedApi> | null = null;

/**
 * Injects embeds.js once and resolves with `window.NavatticEmbed`.
 * Safe to call repeatedly; a failed load (ad blocker, offline) resets the cache
 * so the next user click can retry.
 */
export function loadNavatticEmbeds(): Promise<NavatticEmbedApi> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Navattic embeds can only be loaded in the browser"),
    );
  }

  if (window.NavatticEmbed) return Promise.resolve(window.NavatticEmbed);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<NavatticEmbedApi>((resolve, reject) => {
    let script = document.getElementById(
      NAVATTIC_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    const createdHere = !script;

    if (!script) {
      script = document.createElement("script");
      script.id = NAVATTIC_SCRIPT_ID;
      script.src = NAVATTIC_EMBED_SCRIPT_SRC;
      script.async = true;
    }

    const cleanup = () => {
      window.clearTimeout(timer);
      script?.removeEventListener("load", onLoad);
      script?.removeEventListener("error", onError);
    };

    const fail = (message: string) => {
      cleanup();
      scriptPromise = null;
      // A dead <script> element would swallow the next attempt's listeners.
      if (createdHere) script?.remove();
      reject(new Error(message));
    };

    const onLoad = () => {
      cleanup();
      if (window.NavatticEmbed) {
        resolve(window.NavatticEmbed);
      } else {
        fail(
          "Navattic embeds script loaded but window.NavatticEmbed is missing",
        );
      }
    };

    const onError = () => fail(`Failed to load ${NAVATTIC_EMBED_SCRIPT_SRC}`);

    const timer = window.setTimeout(
      () => fail(`Timed out loading ${NAVATTIC_EMBED_SCRIPT_SRC}`),
      SCRIPT_LOAD_TIMEOUT_MS,
    );

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);

    if (createdHere) document.head.appendChild(script);
  });

  return scriptPromise;
}

/** Configuration applied to each demo's lightbox (per page lifetime). */
const initialisedDemos = new Map<string, InitConfig>();
/** One-shot listeners resolved when Navattic reports the lightbox closed. */
const closeListeners = new Map<string, Set<() => void>>();
/** Guards against a double click racing the script download. */
let activeDemoId: string | null = null;

function notifyClosed(demoId: string) {
  if (activeDemoId === demoId) activeDemoId = null;

  const listeners = closeListeners.get(demoId);
  if (!listeners) return;
  closeListeners.delete(demoId);

  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("[navattic] close listener failed", error);
    }
  });
}

type InitOptions = Pick<NavatticPopupOptions, "title" | "allow">;
type InitConfig = { url: string; title: string; allow: string };

function sameConfig(a: InitConfig, b: InitConfig): boolean {
  return a.url === b.url && a.title === b.title && a.allow === b.allow;
}

/**
 * Creates the `<navattic-lightbox>` for a demo, once per configuration.
 * Navattic keys lightboxes by the last URL segment and applies `title`/`allow`
 * only when the element is created (`configure()` merges the config but does
 * not re-render the header), so a button with a different title for the same
 * demo — or the same id reached from another route — gets a fresh element.
 * `initPopup` is idempotent on Navattic's side, which also re-attaches our
 * `onClose` after an HMR reload. `preload: false` keeps the iframe out of the
 * page until the first open.
 */
function ensureInitialised(
  api: NavatticEmbedApi,
  url: string,
  { title, allow }: InitOptions,
): string {
  const demoId = getNavatticDemoId(url);
  if (!demoId) {
    throw new Error(`[navattic] Cannot extract a demo id from "${url}"`);
  }

  const config: InitConfig = {
    url,
    title: title ?? "",
    allow: allow ?? NAVATTIC_DEFAULT_ALLOW,
  };
  const existing = initialisedDemos.get(demoId);

  if (existing) {
    if (sameConfig(existing, config)) return demoId;
    // Never swap the element under a demo that is open right now.
    if (activeDemoId === demoId) return demoId;
    getLightboxElement(demoId)?.remove();
    closeListeners.delete(demoId);
  }

  api.initPopup(url, {
    title: config.title,
    allow: config.allow,
    preload: false,
    onClose: () => notifyClosed(demoId),
  });
  initialisedDemos.set(demoId, config);
  applyMobileLayout(getLightboxElement(demoId));

  return demoId;
}

/**
 * Locks page scroll behind the lightbox (Navattic does not do this itself).
 * Same technique as `hooks/useBodyScrollLock`, but imperative so it can be
 * driven from a plain click handler. Returns the release function.
 */
export function lockBodyScroll(): () => void {
  const body = document.body;
  const scrollY = window.scrollY;
  const previous = {
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
    overflow: body.style.overflow,
  };

  body.style.position = "fixed";
  body.style.top = `-${scrollY}px`;
  body.style.width = "100%";
  body.style.overflow = "hidden";

  let released = false;
  return () => {
    if (released) return;
    released = true;

    body.style.position = previous.position;
    body.style.top = previous.top;
    body.style.width = previous.width;
    body.style.overflow = previous.overflow;

    window.scrollTo({ top: scrollY, left: 0, behavior: "instant" });
  };
}

export type PreloadNavatticDemoOptions = {
  url: string;
  title?: string;
  allow?: string;
};

/**
 * Downloads the embed script and creates the (empty) lightbox ahead of a click,
 * e.g. on hover/focus. The demo iframe itself is still deferred to the first open.
 */
export async function preloadNavatticDemo({
  url,
  title,
  allow,
}: PreloadNavatticDemoOptions): Promise<void> {
  if (!isAllowedNavatticUrl(url)) return;
  const api = await loadNavatticEmbeds();
  ensureInitialised(api, url, { title, allow });
}

export type OpenNavatticDemoOptions = PreloadNavatticDemoOptions & {
  /** Lock page scroll while the demo is open. Default `true`. */
  lockScroll?: boolean;
  /** Fires once the lightbox is open. Receives the `<navattic-lightbox>` element. */
  onOpen?: (lightbox: HTMLElement) => void;
  /** Fires once, when the visitor closes the demo (close button, backdrop, Escape). */
  onClose?: () => void;
  /**
   * Element that receives focus again when the demo closes. Pass the click
   * event's `currentTarget`; pointer clicks do not focus buttons in every
   * browser, so `document.activeElement` (the fallback) may point elsewhere.
   */
  trigger?: HTMLElement | null;
  /**
   * Abort a pending open. The trigger passes the signal of a controller it
   * aborts on unmount: after a client-side navigation the document stays
   * alive, so an open that is still waiting for embeds.js must not lock and
   * show the demo on the page the visitor navigated to.
   */
  signal?: AbortSignal;
};

/**
 * Makes everything around the lightbox inert (unfocusable, hidden from
 * assistive tech, no pointer events) and moves focus to its close button.
 * Navattic's `openPopup` only toggles visibility: no dialog semantics, no
 * focus trap. Returns the function that undoes both.
 */
function trapFocusInLightbox(lightbox: HTMLElement): () => void {
  const madeInert: Element[] = [];
  for (const child of Array.from(document.body.children)) {
    if (child === lightbox || child.hasAttribute("inert")) continue;
    child.setAttribute("inert", "");
    madeInert.push(child);
  }

  const closeButton = lightbox.shadowRoot?.getElementById("close-demo");
  window.requestAnimationFrame(() => {
    (closeButton ?? lightbox).focus?.({ preventScroll: true });
  });

  return () => {
    madeInert.forEach((el) => el.removeAttribute("inert"));
  };
}

/**
 * Opens a Navattic demo in Navattic's own lightbox on the current page.
 * Rejects when the URL is not an allowed Navattic URL or the script cannot load;
 * see `openNavatticDemoOrFallback` for the user-facing wrapper.
 */
export async function openNavatticDemo({
  url,
  title,
  allow,
  lockScroll = true,
  onOpen,
  onClose,
  trigger: explicitTrigger,
  signal,
}: OpenNavatticDemoOptions): Promise<void> {
  if (!isAllowedNavatticUrl(url)) {
    throw new Error(
      `[navattic] Refusing to open a non-Navattic URL: ${String(url)}`,
    );
  }

  // Captured before any await. Callers without an event (plain onClick
  // handlers) fall back to whatever is focused right now.
  const trigger =
    explicitTrigger ??
    (document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null);

  const api = await loadNavatticEmbeds();

  // The trigger went away (client-side navigation) while the script loaded:
  // touching the DOM now would open the demo on a different page.
  if (signal?.aborted) return;

  const demoId = ensureInitialised(api, url, { title, allow });

  // A second click that raced the script download must not lock twice.
  if (activeDemoId) return;

  const lightbox = getLightboxElement(demoId);
  if (!lightbox) {
    throw new Error(`[navattic] Lightbox for "${demoId}" was not created`);
  }

  const release = lockScroll ? lockBodyScroll() : null;
  activeDemoId = demoId;

  const listeners = closeListeners.get(demoId) ?? new Set<() => void>();
  closeListeners.set(demoId, listeners);
  let releaseFocusTrap: (() => void) | null = null;
  const onClosed = () => {
    releaseFocusTrap?.();
    release?.();
    onClose?.();
    if (trigger?.isConnected) {
      window.requestAnimationFrame(() =>
        trigger.focus({ preventScroll: true }),
      );
    }
  };
  listeners.add(onClosed);

  try {
    api.openPopup(url);
  } catch (error) {
    // Roll back everything taken above. Otherwise the page stays frozen behind
    // a lightbox that never opened and the activeDemoId guard swallows every
    // later click, while the caller's fallback opens the demo in a new tab.
    listeners.delete(onClosed);
    if (listeners.size === 0) closeListeners.delete(demoId);
    activeDemoId = null;
    release?.();
    throw error;
  }

  releaseFocusTrap = trapFocusInLightbox(lightbox);

  // The demo is open at this point: a failing consumer callback must neither
  // trigger the new-tab fallback nor undo the scroll lock (onClose releases it).
  try {
    onOpen?.(lightbox);
  } catch (error) {
    console.error("[navattic] onOpen listener failed", error);
  }
}

/**
 * What the buttons call. If the modal cannot be shown (script blocked by an ad
 * blocker, network failure, ...) the demo opens in a new tab instead, so the CTA
 * never dead-ends. Non-Navattic URLs are never opened anywhere.
 *
 * The fallback runs after an async failure, so the click's user activation may
 * already be gone and the browser may block the new tab. `window.open` is
 * called without `noopener` (which would always return null) so a blocked
 * popup is detectable, and same-tab navigation is the last resort.
 */
export async function openNavatticDemoOrFallback(
  options: OpenNavatticDemoOptions,
): Promise<void> {
  try {
    await openNavatticDemo(options);
  } catch (error) {
    console.warn(
      "[navattic] Could not open the demo in a modal, falling back to a new tab.",
      error,
    );
    if (
      typeof window === "undefined" ||
      options.signal?.aborted ||
      !isAllowedNavatticUrl(options.url)
    ) {
      return;
    }
    const tab = window.open(options.url, "_blank");
    if (tab) {
      tab.opener = null;
    } else {
      window.location.assign(options.url);
    }
  }
}
